import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, API_BASE } from "../api/client";
import { useI18n, formatDateTime, formatDate, formatMoney } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Card, Badge, EmptyState } from "../components/shared";
import BulkArchiveDialog, { ArchiveRowButton, ArchiveTabs } from "../components/Archive";
import {
  FileText, Plus, X, Trash2, Wallet, Search, AlertTriangle, Archive,
  Eye, Download, Pencil, Stethoscope, Pill, User, TrendingUp, TrendingDown, Clock,
} from "lucide-react";
import { toast } from "sonner";

const INPUT =
  "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-12 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";

const TYPE_META = {
  consultation: { labelKey: "invoices.typeConsultation", icon: Stethoscope, cls: "bg-brand/10 text-brand" },
  consultation_meds: { labelKey: "invoices.typeConsultationMeds", icon: Stethoscope, cls: "bg-[#2563eb]/10 text-[#2563eb]" },
  medications_only: { labelKey: "invoices.typeMedsOnly", icon: Pill, cls: "bg-terracotta/10 text-terracotta" },
  other: { labelKey: "invoices.typeOther", icon: FileText, cls: "bg-subtle text-ink" },
};

export default function Invoices() {
  const { t } = useI18n();
  const { tenant, user } = useAuth();
  const cur = tenant?.currency || "XOF";
  const canEdit = user && ["owner", "secretary", "cashier"].includes(user.role);
  const canPay = user && ["owner", "secretary", "cashier"].includes(user.role);

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [patients, setPatients] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all"); // all | paid | unpaid | partial | overdue
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);

  const [detailInv, setDetailInv] = useState(null);
  const [payInv, setPayInv] = useState(null);
  const [payForm, setPayForm] = useState({ amount: 0, method: "cash", reference: "" });
  const [archiveView, setArchiveView] = useState("active");
  const [archivedCount, setArchivedCount] = useState(0);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);

  function emptyForm() {
    return { patient_id: "", consultation_id: null, items: [], discount: 0, notes: "" };
  }

  const loadList = async () => {
    const [invRes, sumRes, archRes] = await Promise.all([
      api.get("/invoices", { params: { archived: archiveView === "archived" ? "true" : undefined } }),
      api.get("/invoices/summary"),
      api.get("/invoices", { params: { archived: "true" } }),
    ]);
    setItems(invRes.data);
    setSummary(sumRes.data);
    setArchivedCount(archRes.data.length);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const [p, pr] = await Promise.all([
        api.get("/patients", { params: { limit: 500 } }),
        api.get("/stock/products"),
      ]);
      setPatients(p.data);
      setProducts(pr.data);
      await loadList();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveView]);

  // support ?patient_id=... deep link to create invoice
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const pid = searchParams.get("patient_id");
    if (pid) {
      setForm((f) => ({ ...f, patient_id: pid }));
      setOpenNew(true);
      const next = new URLSearchParams(searchParams);
      next.delete("patient_id");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Derived filtering ----------
  const filtered = useMemo(() => {
    let list = items;
    if (filter === "overdue") {
      list = list.filter((i) => (i.days_unpaid || 0) > 7 && i.status !== "paid");
    } else if (filter !== "all") {
      list = list.filter((i) => i.status === filter);
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((i) => {
        const p = i.patient || {};
        return (
          (i.invoice_number || "").toLowerCase().includes(s) ||
          (p.first_name || "").toLowerCase().includes(s) ||
          (p.last_name || "").toLowerCase().includes(s) ||
          (p.phone || "").toLowerCase().includes(s)
        );
      });
    }
    return list;
  }, [items, filter, search]);

  // ---------- Form helpers ----------
  const addItem = (type) =>
    setForm({
      ...form,
      items: [...form.items, { type, name: "", quantity: 1, unit_price: 0, ref_id: null }],
    });
  const updateItem = (i, patch) => {
    const next = [...form.items];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, items: next });
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, k) => k !== i) });

  const subtotal = useMemo(
    () => form.items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0),
    [form.items]
  );
  const total = Math.max(0, subtotal - (parseFloat(form.discount) || 0));

  const openEdit = async (inv) => {
    try {
      const full = await api.get(`/invoices/${inv.id}`);
      const d = full.data;
      setForm({
        patient_id: d.patient_id,
        consultation_id: d.consultation_id || null,
        items: (d.items || []).map((it) => ({
          type: it.type,
          name: it.name,
          quantity: it.quantity,
          unit_price: it.unit_price,
          ref_id: it.ref_id || null,
        })),
        discount: d.discount || 0,
        notes: d.notes || "",
      });
      setEditingId(d.id);
      setOpenNew(true);
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      patient_id: form.patient_id,
      consultation_id: form.consultation_id || null,
      items: form.items.map((it) => ({
        type: it.type,
        name: it.name,
        quantity: parseFloat(it.quantity),
        unit_price: parseFloat(it.unit_price),
        ref_id: it.ref_id || null,
      })),
      discount: parseFloat(form.discount) || 0,
      notes: form.notes,
    };
    try {
      if (editingId) await api.patch(`/invoices/${editingId}`, payload);
      else await api.post("/invoices", payload);
      toast.success(t("common.success"));
      setForm(emptyForm());
      setEditingId(null);
      setOpenNew(false);
      loadList();
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payments", {
        invoice_id: payInv.id,
        amount: parseFloat(payForm.amount),
        method: payForm.method,
        reference: payForm.reference || null,
      });
      toast.success(t("common.success"));
      setPayInv(null);
      setPayForm({ amount: 0, method: "cash", reference: "" });
      // if the detail modal is open on the same invoice, refresh it
      if (detailInv && detailInv.id === payInv.id) {
        const r = await api.get(`/invoices/${payInv.id}`);
        setDetailInv(r.data);
      }
      loadList();
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const token = localStorage.getItem("mediflow_token");
      const res = await fetch(`${API_BASE}/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("PDF error");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const openDetail = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}`);
      setDetailInv(r.data);
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  // ---------- Render ----------
  return (
    <div>
      <PageHeader
        title={t("invoices.title")}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <ArchiveTabs value={archiveView} onChange={setArchiveView} activeCount={archiveView === "active" ? items.length : null} archivedCount={archivedCount} />
            {canEdit && archiveView === "active" && (
              <>
                <button onClick={() => setBulkArchiveOpen(true)} data-testid="bulk-archive-btn"
                  className="h-11 px-4 rounded-xl border border-[var(--border-default)] bg-white hover:bg-subtle font-semibold text-sm flex items-center gap-2">
                  <Archive className="w-4 h-4" /> Archiver…
                </button>
                <button
                  onClick={() => { setEditingId(null); setForm(emptyForm()); setOpenNew(true); }}
                  data-testid="new-invoice-btn"
                  className="h-12 px-5 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" /> {t("invoices.new")}
                </button>
              </>
            )}
          </div>
        }
      />

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="invoices-summary">
        <SummaryCard
          testid="sum-invoiced"
          icon={TrendingUp}
          tone="brand"
          label={t("invoices.summaryInvoicedToday")}
          value={formatMoney(summary?.invoiced_today || 0, cur)}
          hint={`${summary?.invoiced_today_count || 0} ${t("invoices.title").toLowerCase()}`}
        />
        <SummaryCard
          testid="sum-collected"
          icon={TrendingDown}
          tone="success"
          label={t("invoices.summaryCollectedToday")}
          value={formatMoney(summary?.collected_today || 0, cur)}
        />
        <SummaryCard
          testid="sum-unpaid"
          icon={AlertTriangle}
          tone={(summary?.overdue_count || 0) > 0 ? "danger" : "warning"}
          label={t("invoices.summaryUnpaid")}
          value={formatMoney(summary?.unpaid_total || 0, cur)}
          hint={
            summary
              ? `${summary.unpaid_count} ${t("invoices.status.unpaid").toLowerCase()} · ${summary.overdue_count} ${t("invoices.filterOverdue").toLowerCase()}`
              : ""
          }
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="invoices-filters">
        <div className="flex flex-wrap gap-2">
          <FilterChip id="all" label={t("invoices.filterAll")} active={filter === "all"} onClick={setFilter} count={items.length} />
          <FilterChip id="unpaid" label={t("invoices.filterUnpaid")} active={filter === "unpaid"} onClick={setFilter} count={items.filter((i) => i.status === "unpaid").length} tone="danger" />
          <FilterChip id="partial" label={t("invoices.filterPartial")} active={filter === "partial"} onClick={setFilter} count={items.filter((i) => i.status === "partial").length} tone="warning" />
          <FilterChip id="paid" label={t("invoices.filterPaid")} active={filter === "paid"} onClick={setFilter} count={items.filter((i) => i.status === "paid").length} tone="success" />
          <FilterChip id="overdue" label={t("invoices.filterOverdue")} active={filter === "overdue"} onClick={setFilter} count={items.filter((i) => (i.days_unpaid || 0) > 7 && i.status !== "paid").length} tone="danger" />
        </div>
        <div className="relative sm:ml-auto sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            data-testid="inv-search"
            placeholder={t("patients.searchPh")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${INPUT} pl-10`}
          />
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-ink-muted">{t("common.loading")}</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={FileText} title={t("common.noData")} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-subtle text-xs uppercase tracking-wider text-ink-muted">
                  <th className="text-left py-3 px-5 font-semibold">{t("invoices.number")}</th>
                  <th className="text-left py-3 px-5 font-semibold">{t("appointments.patient")}</th>
                  <th className="text-left py-3 px-5 font-semibold">{t("invoices.type")}</th>
                  <th className="text-left py-3 px-5 font-semibold">{t("common.date")}</th>
                  <th className="text-right py-3 px-5 font-semibold">{t("common.total")}</th>
                  <th className="text-right py-3 px-5 font-semibold">{t("invoices.balance")}</th>
                  <th className="text-center py-3 px-5 font-semibold">{t("common.status")}</th>
                  <th className="text-right py-3 px-5 font-semibold">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => {
                  const overdue = (inv.days_unpaid || 0) > 7 && inv.status !== "paid";
                  return (
                    <tr
                      key={inv.id}
                      className={`border-t border-[var(--border-default)] cursor-pointer transition ${overdue ? "bg-[#d34044]/5 hover:bg-[#d34044]/10" : "hover:bg-subtle/50"}`}
                      onClick={() => openDetail(inv)}
                      data-testid={`inv-${inv.id}`}
                    >
                      <td className="py-3 px-5 font-mono text-sm text-ink">{inv.invoice_number}</td>
                      <td className="py-3 px-5">
                        {inv.patient ? (
                          <div>
                            <div className="font-medium text-ink">{inv.patient.first_name} {inv.patient.last_name}</div>
                            {inv.patient.phone && <div className="text-xs text-ink-muted">{inv.patient.phone}</div>}
                          </div>
                        ) : <span className="text-ink-muted">—</span>}
                      </td>
                      <td className="py-3 px-5"><TypeBadge type={inv.type} t={t} /></td>
                      <td className="py-3 px-5 text-sm text-ink-muted">{formatDateTime(inv.created_at)}</td>
                      <td className="py-3 px-5 text-right stat-number font-bold">{formatMoney(inv.total, cur)}</td>
                      <td className={`py-3 px-5 text-right stat-number ${inv.balance > 0 ? "text-[#b12e31] font-bold" : "text-ink-muted"}`}>
                        {formatMoney(inv.balance, cur)}
                      </td>
                      <td className="py-3 px-5 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <Badge tone={inv.status === "paid" ? "success" : inv.status === "partial" ? "warning" : "danger"}>
                            {t(`invoices.status.${inv.status}`)}
                          </Badge>
                          {inv.status !== "paid" && inv.days_unpaid > 0 && (
                            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${overdue ? "text-[#b12e31]" : "text-ink-muted"}`}>
                              <Clock className="w-3 h-3" />
                              {t("invoices.overdueWarning").replace("{n}", inv.days_unpaid)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-5 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          <IconBtn testid={`inv-view-${inv.id}`} title={t("invoices.actView")} onClick={() => openDetail(inv)}>
                            <Eye className="w-4 h-4" />
                          </IconBtn>
                          {canPay && inv.status !== "paid" && (
                            <button
                              onClick={() => { setPayInv(inv); setPayForm({ amount: inv.balance, method: "cash", reference: "" }); }}
                              data-testid={`inv-pay-${inv.id}`}
                              className="inline-flex items-center gap-1 px-3 h-9 rounded-lg bg-brand text-white text-xs font-semibold hover:bg-brand-hover"
                            >
                              <Wallet className="w-3.5 h-3.5" /> {t("invoices.payNow")}
                            </button>
                          )}
                          <IconBtn testid={`inv-pdf-${inv.id}`} title={t("invoices.actPdf")} onClick={() => downloadPdf(inv)}>
                            <Download className="w-4 h-4" />
                          </IconBtn>
                          {canEdit && inv.amount_paid === 0 && (
                            <IconBtn testid={`inv-edit-${inv.id}`} title={t("invoices.actEdit")} onClick={() => openEdit(inv)}>
                              <Pencil className="w-4 h-4" />
                            </IconBtn>
                          )}
                          {canEdit && (inv.status === "paid" || archiveView === "archived") && (
                            <ArchiveRowButton kind="invoices" itemId={inv.id} archived={inv.archived || archiveView === "archived"} onDone={loadList} />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* New / Edit Invoice modal */}
      {openNew && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)] sticky top-0 bg-white">
              <h3 className="font-heading font-bold text-xl">
                {editingId ? t("invoices.actEdit") : t("invoices.new")}
              </h3>
              <button onClick={() => { setOpenNew(false); setEditingId(null); }} className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-5">
              <div>
                <label className="block mb-2 text-sm font-semibold">{t("appointments.patient")}</label>
                <select required className={INPUT} value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} data-testid="inv-patient">
                  <option value="">—</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                </select>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="font-heading font-bold text-ink mr-auto">{t("invoices.items")}</div>
                  <button type="button" data-testid="inv-add-service" onClick={() => addItem("service")} className="px-3 py-1.5 rounded-lg bg-brand/10 text-brand text-xs font-semibold">+ {t("invoices.service")}</button>
                  <button type="button" data-testid="inv-add-medication" onClick={() => addItem("medication")} className="px-3 py-1.5 rounded-lg bg-terracotta/10 text-terracotta text-xs font-semibold">+ {t("invoices.medication")}</button>
                </div>
                <div className="space-y-3">
                  {form.items.map((it, i) => (
                    <div key={i} className="p-3 rounded-xl border border-[var(--border-default)] grid grid-cols-1 sm:grid-cols-6 gap-2" data-testid={`inv-item-${i}`}>
                      {it.type === "medication" ? (
                        <select className={`${INPUT} h-11 sm:col-span-3`} data-testid={`inv-item-ref-${i}`} value={it.ref_id || ""} onChange={(e) => {
                          const p = products.find((x) => x.id === e.target.value);
                          updateItem(i, { ref_id: e.target.value, name: p?.name || it.name, unit_price: p?.sale_price ?? it.unit_price });
                        }}>
                          <option value="">— {t("invoices.itemName")}</option>
                          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      ) : (
                        <input className={`${INPUT} h-11 sm:col-span-3`} data-testid={`inv-item-name-${i}`} placeholder={t("invoices.itemName")} value={it.name} onChange={(e) => updateItem(i, { name: e.target.value })} />
                      )}
                      <input type="number" min={0} step="0.01" className={`${INPUT} h-11`} data-testid={`inv-item-qty-${i}`} placeholder={t("invoices.qty")} value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} />
                      <input type="number" min={0} className={`${INPUT} h-11`} data-testid={`inv-item-price-${i}`} placeholder={t("invoices.unitPrice")} value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: e.target.value })} />
                      <div className="flex items-center gap-2 justify-end">
                        <div className="stat-number font-bold text-ink text-right">
                          {formatMoney((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), cur)}
                        </div>
                        <button type="button" onClick={() => removeItem(i)} className="p-2 rounded-lg text-terracotta hover:bg-terracotta/10">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-2 text-sm font-semibold">{t("invoices.discount")}</label>
                  <input type="number" min={0} className={INPUT} value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} data-testid="inv-discount" />
                </div>
                <div className="self-end p-3 rounded-xl bg-subtle text-right">
                  <div className="text-xs text-ink-muted">{t("invoices.subtotal")}: <span className="font-mono">{formatMoney(subtotal, cur)}</span></div>
                  <div className="text-xl font-heading font-extrabold text-ink stat-number">{formatMoney(total, cur)}</div>
                </div>
              </div>

              <div className="flex gap-3 pt-2 sticky bottom-0 bg-white pb-1">
                <button type="button" onClick={() => { setOpenNew(false); setEditingId(null); }} className="flex-1 h-12 rounded-xl border border-[var(--border-default)] font-semibold">{t("common.cancel")}</button>
                <button type="submit" data-testid="inv-save" className="flex-1 h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold">{t("common.save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detailInv && (
        <DetailModal
          inv={detailInv}
          cur={cur}
          t={t}
          onClose={() => setDetailInv(null)}
          onPay={canPay ? () => { setPayInv(detailInv); setPayForm({ amount: detailInv.balance, method: "cash", reference: "" }); } : null}
          onPdf={() => downloadPdf(detailInv)}
          onEdit={canEdit && detailInv.amount_paid === 0 ? () => { setDetailInv(null); openEdit(detailInv); } : null}
        />
      )}

      {/* Payment modal */}
      {payInv && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-xl">{t("payments.new")}</h3>
              <button onClick={() => setPayInv(null)} className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-4 p-4 rounded-xl bg-subtle">
              <div className="text-xs text-ink-muted">{t("invoices.number")}</div>
              <div className="font-mono text-sm">{payInv.invoice_number}</div>
              <div className="mt-2 text-xs text-ink-muted">{t("invoices.balance")}</div>
              <div className="stat-number font-bold text-lg">{formatMoney(payInv.balance, cur)}</div>
            </div>
            <form onSubmit={pay} className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-semibold">{t("payments.amount")}</label>
                <input type="number" min={0} max={payInv.balance} step="0.01" required className={INPUT} value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amount" />
              </div>
              <div>
                <label className="block mb-2 text-sm font-semibold">{t("payments.method")}</label>
                <select className={INPUT} value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} data-testid="pay-method">
                  <option value="cash">{t("payments.cash")}</option>
                  <option value="card">{t("payments.card")}</option>
                  <option value="wave">{t("payments.wave")}</option>
                  <option value="orange_money">{t("payments.orange_money")}</option>
                </select>
              </div>
              <div>
                <label className="block mb-2 text-sm font-semibold">{t("payments.reference")}</label>
                <input className={INPUT} value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} />
              </div>
              <button type="submit" data-testid="pay-submit" className="w-full h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold">
                {t("common.confirm")}
              </button>
            </form>
          </div>
        </div>
      )}
      {bulkArchiveOpen && (
        <BulkArchiveDialog kind="invoices" onClose={() => setBulkArchiveOpen(false)}
          onDone={() => { setBulkArchiveOpen(false); loadList(); }} />
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function SummaryCard({ icon: Icon, tone = "brand", label, value, hint, testid }) {
  const tones = {
    brand: "bg-brand/10 text-brand",
    success: "bg-[#2d8a56]/10 text-[#2d8a56]",
    warning: "bg-[#e3a03b]/15 text-[#8a5a10]",
    danger: "bg-[#d34044]/10 text-[#b12e31]",
  };
  return (
    <Card className="p-5 flex items-start gap-4" data-testid={testid}>
      <div className={`p-3 rounded-xl ${tones[tone]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="min-w-0">
        <div className="text-sm text-ink-muted">{label}</div>
        <div className="stat-number text-2xl font-heading font-extrabold text-ink truncate">{value}</div>
        {hint && <div className="text-xs text-ink-muted mt-0.5">{hint}</div>}
      </div>
    </Card>
  );
}

function FilterChip({ id, label, active, onClick, count, tone = "brand" }) {
  const toneActive = {
    brand: "bg-brand text-white",
    success: "bg-[#2d8a56] text-white",
    warning: "bg-[#e3a03b] text-white",
    danger: "bg-[#d34044] text-white",
  };
  return (
    <button
      data-testid={`filter-${id}`}
      onClick={() => onClick(id)}
      className={`h-10 px-4 rounded-full text-sm font-semibold flex items-center gap-2 transition ${active ? toneActive[tone] : "bg-white border border-[var(--border-default)] text-ink hover:bg-subtle"}`}
    >
      {label}
      <span className={`text-xs px-2 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-subtle text-ink-muted"}`}>{count}</span>
    </button>
  );
}

function TypeBadge({ type, t }) {
  const meta = TYPE_META[type] || TYPE_META.other;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {t(meta.labelKey)}
    </span>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="p-2 rounded-lg text-ink-muted hover:text-ink hover:bg-subtle transition"
    >
      {children}
    </button>
  );
}

function DetailModal({ inv, cur, t, onClose, onPay, onPdf, onEdit }) {
  const overdue = (inv.days_unpaid || 0) > 7 && inv.status !== "paid";
  const hasMeds = (inv.items || []).some((i) => i.type === "medication");
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50" data-testid="inv-detail-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)] sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs text-ink-muted uppercase tracking-wider">{t("invoices.detail")}</div>
            <h3 className="font-heading font-bold text-xl font-mono">{inv.invoice_number}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-subtle" data-testid="inv-detail-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* header status */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={inv.status === "paid" ? "success" : inv.status === "partial" ? "warning" : "danger"}>
              {t(`invoices.status.${inv.status}`)}
            </Badge>
            <TypeBadge type={inv.type} t={t} />
            {overdue && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#d34044]/10 text-[#b12e31]">
                <AlertTriangle className="w-3.5 h-3.5" />
                {t("invoices.overdueWarning").replace("{n}", inv.days_unpaid)}
              </span>
            )}
            <span className="ml-auto text-sm text-ink-muted">{formatDateTime(inv.created_at)}</span>
          </div>

          {/* Patient */}
          <div className="p-4 rounded-xl bg-subtle flex items-start gap-3">
            <User className="w-5 h-5 text-brand mt-0.5" />
            <div className="flex-1">
              <div className="text-xs text-ink-muted uppercase">{t("appointments.patient")}</div>
              <div className="font-semibold text-ink">
                {inv.patient ? `${inv.patient.first_name} ${inv.patient.last_name}` : "—"}
              </div>
              {inv.patient?.phone && <div className="text-sm text-ink-muted">{inv.patient.phone}</div>}
            </div>
          </div>

          {/* Linked consultation */}
          {inv.consultation && (
            <div className="p-4 rounded-xl border border-[var(--border-default)]">
              <div className="flex items-center gap-2 mb-2">
                <Stethoscope className="w-4 h-4 text-brand" />
                <div className="text-sm font-semibold text-ink">{t("invoices.linkedConsultation")}</div>
              </div>
              <div className="text-sm text-ink-muted">
                {formatDate(inv.consultation.created_at)}
                {inv.consultation.doctor && ` · Dr ${inv.consultation.doctor.first_name} ${inv.consultation.doctor.last_name}`}
              </div>
              {inv.consultation.diagnosis && <div className="text-sm mt-1"><strong>{t("consultations.diagnosis")}:</strong> {inv.consultation.diagnosis}</div>}
              {inv.consultation.reason && <div className="text-sm text-ink-muted mt-0.5">{inv.consultation.reason}</div>}
            </div>
          )}

          {/* Items */}
          <div>
            <div className="text-sm font-semibold text-ink mb-2">{t("invoices.items")}</div>
            {(inv.items || []).length === 0 ? (
              <div className="text-sm text-ink-muted">{t("invoices.noItems")}</div>
            ) : (
              <div className="rounded-xl border border-[var(--border-default)] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-subtle">
                    <tr className="text-xs uppercase text-ink-muted">
                      <th className="text-left py-2 px-3">{t("invoices.itemName")}</th>
                      <th className="text-right py-2 px-3">{t("invoices.qty")}</th>
                      <th className="text-right py-2 px-3">{t("invoices.unitPrice")}</th>
                      <th className="text-right py-2 px-3">{t("common.total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inv.items.map((it, i) => (
                      <tr key={i} className="border-t border-[var(--border-default)]">
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            {it.type === "medication" ? <Pill className="w-3.5 h-3.5 text-terracotta" /> : <Stethoscope className="w-3.5 h-3.5 text-brand" />}
                            <span>{it.name}</span>
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right">{it.quantity}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatMoney(it.unit_price, cur)}</td>
                        <td className="py-2 px-3 text-right font-mono font-semibold">{formatMoney(it.total, cur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {hasMeds && (
              <div className="text-xs text-ink-muted mt-2 flex items-center gap-1">
                <Pill className="w-3 h-3" /> {t("consultations.stockLinked")}
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="p-4 rounded-xl bg-subtle space-y-1.5">
            <Row k={t("invoices.subtotal")} v={formatMoney(inv.subtotal, cur)} />
            {inv.discount > 0 && <Row k={t("invoices.discount")} v={`- ${formatMoney(inv.discount, cur)}`} />}
            <Row k={t("common.total")} v={formatMoney(inv.total, cur)} strong />
            <Row k={t("payments.amount")} v={formatMoney(inv.amount_paid, cur)} />
            <Row k={t("invoices.balance")} v={formatMoney(inv.balance, cur)} strong tone={inv.balance > 0 ? "danger" : "success"} />
          </div>

          {/* Payments history */}
          {(inv.payments || []).length > 0 && (
            <div>
              <div className="text-sm font-semibold text-ink mb-2">{t("invoices.paymentsHistory")}</div>
              <div className="space-y-2">
                {inv.payments.map((p) => (
                  <div key={p.id} className="p-3 rounded-xl border border-[var(--border-default)] flex items-center gap-3">
                    <Wallet className="w-4 h-4 text-brand" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{t(`payments.${p.method}`)}</div>
                      <div className="text-xs text-ink-muted">{formatDateTime(p.created_at)} · {p.reference || "—"}</div>
                    </div>
                    <div className="stat-number font-bold">{formatMoney(p.amount, cur)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-4 border-t border-[var(--border-default)] sticky bottom-0 bg-white flex flex-wrap gap-2">
          <button onClick={onPdf} data-testid="inv-detail-pdf" className="flex-1 h-11 rounded-xl border border-[var(--border-default)] font-semibold flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> {t("invoices.actPdf")}
          </button>
          {onEdit && (
            <button onClick={onEdit} data-testid="inv-detail-edit" className="flex-1 h-11 rounded-xl border border-[var(--border-default)] font-semibold flex items-center justify-center gap-2">
              <Pencil className="w-4 h-4" /> {t("invoices.actEdit")}
            </button>
          )}
          {onPay && inv.status !== "paid" && (
            <button onClick={onPay} data-testid="inv-detail-pay" className="flex-1 h-11 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold flex items-center justify-center gap-2">
              <Wallet className="w-4 h-4" /> {t("invoices.payNow")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, strong, tone }) {
  const toneCls = tone === "danger" ? "text-[#b12e31]" : tone === "success" ? "text-[#2d8a56]" : "text-ink";
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${strong ? "font-semibold text-ink" : "text-ink-muted"}`}>{k}</span>
      <span className={`stat-number ${strong ? "font-bold text-base" : "text-sm"} ${toneCls}`}>{v}</span>
    </div>
  );
}
